import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Trash2 } from "lucide-react";
import { api, errorText, fileUrl } from "@/lib/api";

export default function PhotosManager() {
  const [items, setItems] = useState(null);
  const [captionPl, setCaptionPl] = useState("");
  const [captionEn, setCaptionEn] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/photos").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    const files = Array.from(fileRef.current?.files || []);
    if (!files.length) return toast.error("Wybierz zdjęcie.");
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("caption_pl", captionPl);
      fd.append("caption_en", captionEn);
      try {
        const { data } = await api.post("/admin/photos", fd);
        setItems((l) => [data, ...(l || [])]);
      } catch (err) {
        toast.error(`${f.name}: ${errorText(err)}`);
      }
    }
    setBusy(false);
    setCaptionPl("");
    setCaptionEn("");
    fileRef.current.value = "";
    toast.success("Gotowe. Zdjęcia są już na stronie.");
  };

  const remove = async (id) => {
    if (!window.confirm("Usunąć to zdjęcie ze strony?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      setItems((l) => l.filter((p) => p.id !== id));
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  const input = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-zinc-400 focus:border-white focus:outline-none";
  return (
    <section data-testid="admin-photos" className="space-y-8">
      <form onSubmit={upload} data-testid="admin-photo-upload-form" className="rounded-2xl border border-white/10 bg-[#14171E] p-6">
        <p className="font-display text-2xl font-bold uppercase italic">Dodaj zdjęcia do galerii</p>
        <p className="mt-1 text-sm text-zinc-400">JPG, PNG lub WEBP, do 15 MB. Możesz wybrać kilka naraz.</p>
        <input ref={fileRef} data-testid="admin-photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple className="mt-5 block w-full text-sm text-zinc-300 file:mr-4 file:rounded-full file:border-0 file:bg-white file:px-4 file:py-2 file:font-semibold file:text-black" />
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <input data-testid="admin-photo-caption-pl-input" placeholder="Podpis PL (opcjonalnie)" value={captionPl} onChange={(e) => setCaptionPl(e.target.value)} maxLength={120} className={input} />
          <input data-testid="admin-photo-caption-en-input" placeholder="Podpis EN (opcjonalnie)" value={captionEn} onChange={(e) => setCaptionEn(e.target.value)} maxLength={120} className={input} />
        </div>
        <button data-testid="admin-photo-upload-button" disabled={busy} className="mt-5 flex items-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-black hover:bg-zinc-300 disabled:opacity-60">
          <ImagePlus className="h-4 w-4" /> {busy ? "Wysyłanie…" : "Wgraj"}
        </button>
      </form>
      {items === null ? (
        <p className="text-zinc-400">Ładowanie…</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="admin-photos-grid">
          {items.length === 0 && <p className="col-span-full text-sm text-zinc-400">Brak dodanych zdjęć. Na stronie widać na razie 3 zdjęcia startowe.</p>}
          {items.map((p) => (
            <figure key={p.id} data-testid={`admin-photo-${p.id}`} className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#14171E]">
              <img src={fileUrl(p.url)} alt={p.caption_pl || "zdjęcie"} className="h-full w-full object-cover" />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3 text-sm">{p.caption_pl}</figcaption>
              <button data-testid={`admin-photo-delete-${p.id}`} onClick={() => remove(p.id)} className="absolute right-2 top-2 rounded-full bg-black/60 p-2 text-white backdrop-blur hover:bg-red-500" aria-label="Usuń">
                <Trash2 className="h-4 w-4" />
              </button>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
